import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, Volume2, VolumeX, X, BookOpen, Shield, Zap, Star } from 'lucide-react';

const StoryViewer = ({ isOpen, onClose }) => {
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(false);
  const videoRef = useRef(null);
  const controlsTimeoutRef = useRef(null);

  // Handle play/pause state
  useEffect(() => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.play().catch(error => {
          console.error('Error playing video:', error);
        });
      } else {
        videoRef.current.pause();
      }
    }
  }, [isPlaying]);

  const characterStories = [
    {
      id: 'raaghav',
      name: 'Raaghav',
      title: 'The Cyber Intruder',
      description: 'Raaghav is a skilled hacker with a knack for breaching even the most secure systems. He thrives in the shadows of the digital world.',
      color: '#3B82F6',
      accent: '#1E40AF',
      icon: Shield,
      tagline: 'He\'s the Digital Rat',
      abilities: ['Threat Analysis', 'Security Architecture', 'Incident Response'],
      videos: [
        {
          id: 'intro',
          title: 'Introduction',
          description: 'Raaghav is a skilled hacker with a knack for breaching even the most secure systems. He thrives in the shadows of the digital world.',
          path: '/assets/videos/raaghav_intro.mp4',
          duration: '2:30',
          featured: true
        }
      ]
    },
    {
      id: 'sunaya',
      name: 'Shanaya',
      title: 'The Security Analyst',
      description: 'Shanaya is a brilliant professional analyst known for her keen eye for details and efficiency in her work place. She joins Raaghav\'s former employer in the technology team.',
      color: '#EC4899',
      accent: '#BE185D',
      icon: Zap,
      tagline: 'She\'s the Detail Detective',
      abilities: ['Hacking', 'Digital Theif', 'Risk Management'],
      videos: [
        {
          id: 'intro',
          title: 'Introduction',
          description: 'Shanaya is a brilliant professional analyst known for her keen eye for details and efficiency in her work place. She joins Raaghav\'s former employer in the technology team.',
          path: '/assets/videos/shanaya_intro.mp4',
          duration: '2:45',
          featured: true
        }
      ]
    }
  ];

  const handleCharacterSelect = (character) => {
    console.log('Character selected:', character.name);
    setSelectedCharacter(character);
    setCurrentVideoIndex(0);
    setIsAutoPlaying(true);
    setIsPlaying(false);
    setVideoProgress(0);
    
    // Auto-play the first video after a short delay
    setTimeout(() => {
      if (character.videos && character.videos.length > 0) {
        const firstVideo = character.videos[0];
        console.log('Loading first video:', firstVideo.title, firstVideo.path);
        if (videoRef.current) {
          videoRef.current.src = firstVideo.path;
          videoRef.current.load();
          
          // Wait for video to be ready before playing
          videoRef.current.addEventListener('canplay', () => {
            console.log('Video can play, starting playback');
            videoRef.current.play().catch(error => {
              console.error('Error playing video:', error);
            });
            setIsPlaying(true);
          }, { once: true });
        }
      }
    }, 500);
  };

  const handleVideoSelect = (video) => {
    console.log('Manual video selection:', video.title, video.path);
    setIsAutoPlaying(false); // Stop auto-play when manually selecting
    if (videoRef.current) {
      videoRef.current.src = video.path;
      videoRef.current.load();
      
      // Wait for video to be ready before playing
      videoRef.current.addEventListener('canplay', () => {
        console.log('Manual video can play, starting playback');
        videoRef.current.play().catch(error => {
          console.error('Error playing manual video:', error);
        });
        setIsPlaying(true);
      }, { once: true });
      
      setVideoProgress(0);
    }
  };

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const progress = (videoRef.current.currentTime / videoRef.current.duration) * 100;
      setVideoProgress(progress);
    }
  };

  const handleVideoEnd = () => {
    console.log('Video ended, current index:', currentVideoIndex, 'isAutoPlaying:', isAutoPlaying);
    if (isAutoPlaying && selectedCharacter && selectedCharacter.videos) {
      const nextIndex = currentVideoIndex + 1;
      console.log('Next video index:', nextIndex, 'Total videos:', selectedCharacter.videos.length);
      
      if (nextIndex < selectedCharacter.videos.length) {
        // Play next video
        setCurrentVideoIndex(nextIndex);
        const nextVideo = selectedCharacter.videos[nextIndex];
        console.log('Loading next video:', nextVideo.title, nextVideo.path);
        
        setTimeout(() => {
          if (videoRef.current) {
            videoRef.current.src = nextVideo.path;
            videoRef.current.load();
            
            // Wait for video to be ready before playing
            videoRef.current.addEventListener('canplay', () => {
              console.log('Next video can play, starting playback');
              videoRef.current.play().catch(error => {
                console.error('Error playing next video:', error);
              });
              setIsPlaying(true);
            }, { once: true });
          }
        }, 1000); // 1 second delay between videos
      } else {
        // All videos completed
        console.log('All videos completed for', selectedCharacter.name);
        setIsAutoPlaying(false);
        setIsPlaying(false);
        setCurrentVideoIndex(0);
      }
    }
  };

  const handleMouseMove = () => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    controlsTimeoutRef.current = setTimeout(() => {
      setShowControls(false);
    }, 3000);
  };

  const handleBack = () => {
    setSelectedCharacter(null);
    setIsPlaying(false);
    setVideoProgress(0);
    setCurrentVideoIndex(0);
    setIsAutoPlaying(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/90 z-[10000] flex items-center justify-center p-2 sm:p-4">
      <div className="relative w-full max-w-7xl mx-0 sm:mx-4 bg-gradient-to-br from-gray-900 to-black rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl max-h-[96dvh]">
        {/* Header */}
        <div className="relative p-3 sm:p-6 bg-gradient-to-r from-gray-800 to-gray-900">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 sm:space-x-4 min-w-0">
              <BookOpen size={22} className="text-blue-400 shrink-0 sm:w-8 sm:h-8" />
              <div>
                <h2 className="text-lg sm:text-2xl font-bold text-white">Character Stories</h2>
                <p className="text-xs sm:text-base text-gray-300">Explore the stories of your cybersecurity mentors</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-full transition-colors"
            >
              <X size={24} className="text-white" />
            </button>
          </div>
        </div>

        <div className="flex flex-col md:flex-row h-[82dvh] md:h-[70vh] overflow-y-auto">
          {/* Character Selection Sidebar */}
          {!selectedCharacter && (
            <div className="w-full md:w-1/3 bg-gradient-to-b from-gray-800 to-gray-900 p-3 sm:p-6 overflow-y-auto">
              <h3 className="text-base sm:text-xl font-bold text-white mb-4 sm:mb-6">Choose a Character</h3>
              <div className="space-y-4">
                {characterStories.map((character) => {
                  const IconComponent = character.icon;
                  return (
                    <div
                      key={character.id}
                      onClick={() => handleCharacterSelect(character)}
                      className="p-3 sm:p-4 bg-white/5 hover:bg-white/10 rounded-lg cursor-pointer transition-all duration-300 border border-white/10 hover:border-white/20 hover:shadow-lg hover:shadow-blue-500/10 group"
                    >
                      <div className="flex items-center space-x-3 sm:space-x-4">
                        <div 
                          className="w-10 h-10 sm:w-14 sm:h-14 rounded-full flex items-center justify-center text-white font-bold text-lg relative overflow-hidden group-hover:scale-110 transition-transform duration-300"
                          style={{ backgroundColor: character.color }}
                        >
                          <IconComponent size={20} className="text-white sm:w-7 sm:h-7" />
                          <div className="absolute inset-0 rounded-full border-2 border-white/20 group-hover:border-white/40 transition-colors"></div>
                        </div>
                        <div className="flex-1">
                          <h4 className="text-sm sm:text-base font-bold text-white group-hover:text-blue-300 transition-colors">{character.name}</h4>
                          <p className="text-xs sm:text-sm text-gray-300">{character.title}</p>
                          <p className="text-[11px] sm:text-xs font-medium" style={{ color: character.color }}>
                            {character.tagline}
                          </p>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Star size={16} className="text-yellow-400" />
                          <span className="text-xs text-gray-400">4.9</span>
                        </div>
                      </div>
                      <p className="text-xs sm:text-sm text-gray-400 mt-2">{character.description}</p>
                      
                      {/* Character Abilities */}
                      <div className="flex flex-wrap gap-1 mt-3">
                        {character.abilities.slice(0, 3).map((ability, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 text-xs font-medium rounded-full border"
                            style={{ 
                              backgroundColor: `${character.color}20`,
                              borderColor: character.color,
                              color: character.color
                            }}
                          >
                            {ability}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Video Selection and Player */}
          {selectedCharacter && (
            <div className="flex-1 flex flex-col min-h-0">
              {/* Character Header */}
              <div className="p-3 sm:p-4 bg-gradient-to-r from-gray-800 to-gray-900 border-b border-white/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div 
                      className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg"
                      style={{ backgroundColor: selectedCharacter.color }}
                    >
                      {selectedCharacter.name.charAt(0)}
                    </div>
                    <div>
                      <h3 className="text-base sm:text-xl font-bold text-white">{selectedCharacter.name}</h3>
                      <p className="text-xs sm:text-base text-gray-300">{selectedCharacter.title}</p>
                    </div>
                  </div>
                  <button
                    onClick={handleBack}
                    className="px-3 sm:px-4 py-2 text-xs sm:text-base bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
                  >
                    Back to Characters
                  </button>
                </div>
              </div>

              <div className="flex-1 flex flex-col md:flex-row min-h-0">
                {/* Video List */}
                <div className="w-full md:w-1/3 bg-gradient-to-b from-gray-800 to-gray-900 p-3 sm:p-4 overflow-y-auto max-h-[36dvh] md:max-h-none">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-bold text-white">Videos</h4>
                    {isAutoPlaying && (
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                        <span className="text-xs text-green-400 font-mono">AUTO-PLAYING</span>
                      </div>
                    )}
                  </div>
                  <div className="space-y-3">
                    {selectedCharacter.videos.map((video, index) => (
                      <div
                        key={video.id}
                        onClick={() => handleVideoSelect(video)}
                        className={`p-3 rounded-lg cursor-pointer transition-all duration-300 border ${
                          index === currentVideoIndex && isAutoPlaying
                            ? 'bg-gradient-to-r from-green-500/20 to-blue-500/20 border-green-400/50 hover:border-green-400'
                            : video.featured 
                            ? 'bg-gradient-to-r from-blue-500/20 to-purple-500/20 border-blue-400/50 hover:border-blue-400' 
                            : 'bg-white/5 hover:bg-white/10 border-white/10 hover:border-white/20'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h5 className="font-medium text-white">{video.title}</h5>
                          <div className="flex items-center space-x-2">
                            {index === currentVideoIndex && isAutoPlaying && (
                              <div className="flex items-center space-x-1">
                                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                                <span className="text-xs text-green-400 font-mono">NOW PLAYING</span>
                              </div>
                            )}
                            {video.featured && (
                              <div className="flex items-center space-x-1">
                                <Star size={14} className="text-yellow-400" />
                                <span className="text-xs text-yellow-400 font-medium">Featured</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <p className="text-sm text-gray-400 mb-2">{video.description}</p>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500">{video.duration}</span>
                          {video.featured && (
                            <span className="text-xs px-2 py-1 rounded-full bg-blue-500/20 text-blue-300 border border-blue-400/30">
                              Recommended
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Video Player */}
                <div className="flex-1 bg-black relative min-h-[32dvh] md:min-h-0">
                  <video
                    ref={videoRef}
                    className="w-full h-full object-contain"
                    onTimeUpdate={handleTimeUpdate}
                    onEnded={handleVideoEnd}
                    muted={isMuted}
                    preload="metadata"
                  >
                    Your browser does not support the video tag.
                  </video>

                  {/* Video Controls Overlay */}
                  {showControls && (
                    <div 
                      className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"
                      onMouseMove={handleMouseMove}
                    >
                      <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-6">
                        {/* Progress Bar */}
                        <div className="mb-4">
                          <div className="w-full bg-white/20 rounded-full h-1">
                            <div 
                              className="h-1 rounded-full transition-all duration-300"
                              style={{ 
                                width: `${videoProgress}%`,
                                backgroundColor: selectedCharacter.color 
                              }}
                            />
                          </div>
                        </div>

                        {/* Control Buttons */}
                        <div className="flex items-center justify-center space-x-4">
                          <button
                            onClick={togglePlayPause}
                            className="p-3 bg-white/20 hover:bg-white/30 rounded-full transition-colors"
                          >
                            {isPlaying ? <Pause size={20} className="text-white" /> : <Play size={20} className="text-white" />}
                          </button>
                          
                          <button
                            onClick={toggleMute}
                            className="p-3 bg-white/20 hover:bg-white/30 rounded-full transition-colors"
                          >
                            {isMuted ? <VolumeX size={20} className="text-white" /> : <Volume2 size={20} className="text-white" />}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Character Selection (when no character selected) */}
          {!selectedCharacter && (
            <div className="hidden md:flex flex-1 bg-gradient-to-br from-gray-800 to-gray-900 p-8 items-center justify-center">
              <div className="text-center">
                <BookOpen size={64} className="text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">Select a Character</h3>
                <p className="text-gray-400">Choose a character from the sidebar to explore their story</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StoryViewer;